import type { AvailabilityProduct } from '@travelity/api/services';
import {
    BookingStatus,
    DirectSource,
    DiscountType,
    type NoteVisibility,
    type Customer,
    type PaxData,
    ProductType,
    SourceType,
    type PersonNameData,
    TimezoneName,
} from '@travelity/api';
import { format } from 'date-fns';
import type {
    CreateBookingReqProductOptionsItemCustomersItemDto,
    ComputePackagePriceBookingsReqDto,
    CreatePackageBookingsReqDto,
    CreatePackageBookingsReqItemDto,
    DiscountData,
    DualCurrencyData,
    GetProductsResItem0PricingItemDto,
    PeriodData,
} from '@travelity/api/generated';
import {
    buildCapacityFormRows,
    type CapacityFormRow,
    getPackageItemRowId,
} from './components/PackageCapacity/capacity-form.helpers';

export interface PackageItemOptionCatalogItem {
    id: string;
    name: string;
    type?: string;
    description?: string;
    pricing?: {
        currency?: string | { abbr?: string };
        price?: DualCurrencyData;
        items?: GetProductsResItem0PricingItemDto[];
    };
}

export interface SelectedProductOption {
    optionId: string;
    pax: PaxData;
}

export interface PackageBookingItemMeta {
    key: string;
    time: number;
    product: CreatePackageBookingsReqItemDto['product'];
    day_index: number;
    order_index: number;
    start: number;
    vehicleToCapacityOptionId: Record<string, string>;
    optionsCatalog: PackageItemOptionCatalogItem[];
}

export interface PackageBookingFormValues {
    package_product_id: string;
    status: BookingStatus;
    date?: PeriodData;
    selectedTime: string;
    pax: PaxData;
    customer?: Customer;
    notes: { value: string; type: NoteVisibility }[];
    discount: DiscountData;
    vehicleByProductId: Record<string, string>;
    selectedOptionsByItemKey: Record<string, SelectedProductOption[]>;
    sourceType: SourceType;
    sourceName?: DirectSource;
    referral?: {
        id?: string;
        name?: PersonNameData;
        email: string;
        company?: string;
    };
}

export interface PackageAvailableTimeOption {
    value: string;
    label: string;
}

export const buildPackageAvailableTimeOptions = (
    selectedEvent: AvailabilityProduct
): PackageAvailableTimeOption[] => {
    const uniqueTimes = Array.from(
        new Set(selectedEvent.events.map((event) => event.time))
    );

    return uniqueTimes
        .sort((a, b) => a - b)
        .map((timestamp) => ({
            value: String(timestamp),
            label: format(timestamp, 'HH:mm'),
        }));
};

const resolveDefaultSelectedTime = (
    selectedTime: number,
    options: PackageAvailableTimeOption[]
): string => {
    const selectedTimeValue = String(selectedTime);
    if (options.some((option) => option.value === selectedTimeValue)) {
        return selectedTimeValue;
    }

    const selectedTimeLabel = format(selectedTime, 'HH:mm');
    const labelMatch = options.find(
        (option) => option.label === selectedTimeLabel
    );
    return labelMatch?.value || options[0]?.value || '';
};

const getTimezoneOffset = (
    timeZoneName: string,
    timestamp: number
): number | undefined => {
    try {
        const formatted = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZoneName,
            timeZoneName: 'shortOffset',
        }).formatToParts(new Date(timestamp * 1000));
        const offsetLabel = formatted.find(
            (part) => part.type === 'timeZoneName'
        )?.value;

        if (!offsetLabel || offsetLabel === 'GMT') return 0;

        const match = offsetLabel.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
        if (!match) return undefined;

        const sign = match[1] === '-' ? -1 : 1;
        const hours = Number(match[2]);
        const minutes = Number(match[3] || 0);
        return sign * (hours + minutes / 60);
    } catch {
        return undefined;
    }
};

const normalizeOption = (
    option: unknown
): PackageItemOptionCatalogItem | null => {
    if (!option || typeof option !== 'object') return null;

    const candidate = option as {
        id?: string;
        name?: string;
        type?: string;
        description?: string;
        reference?: { name?: string };
        pricing?: {
            currency?: string | { abbr?: string };
            price?: DualCurrencyData;
            items?: GetProductsResItem0PricingItemDto[];
        };
    };

    if (!candidate.id) return null;

    return {
        id: candidate.id,
        name: candidate.name || candidate.reference?.name || '',
        type: candidate?.type,
        description: candidate.description,
        pricing: candidate.pricing,
    };
};

const getPackageItemOptionsCatalog = (
    item: NonNullable<
        AvailabilityProduct['events'][number]['event']
    >['plan']['items'][number]
): PackageItemOptionCatalogItem[] => {
    const runtimeItem = item as typeof item & {
        options?: PackageItemOptionCatalogItem[] | { items?: unknown[] };
    };

    const rawOptions = Array.isArray(runtimeItem.options)
        ? runtimeItem.options
        : runtimeItem.options?.items || [];

    return rawOptions
        .map(normalizeOption)
        .filter((option): option is PackageItemOptionCatalogItem => !!option);
};

export const buildPackageItemsMeta = (
    selectedEvent: AvailabilityProduct
): PackageBookingItemMeta[] => {
    return selectedEvent.events.flatMap((event) => {
        const eventProducts = event.event?.plan.items || [];

        return eventProducts.map((product) => {
            const capacities = product.events.new.capacities || [];

            return {
                key: getPackageItemRowId(event, product),
                time: event.time,
                product: {
                    type: product.product?.type as ProductType,
                    id: product.product?.id,
                    name: product.product?.name || selectedEvent.name,
                },
                day_index: product.day_index,
                order_index: product.order_index,
                start: product.start,
                vehicleToCapacityOptionId: capacities.reduce<
                    Record<string, string>
                >((acc, capacity) => {
                    acc[capacity.id] = capacity.id;
                    return acc;
                }, {}),
                optionsCatalog: getPackageItemOptionsCatalog(product),
            };
        });
    });
};

const buildSelectedOptionsByItemKeyDefaults = (
    itemsMeta: PackageBookingItemMeta[]
): Record<string, SelectedProductOption[]> => {
    return itemsMeta.reduce<Record<string, SelectedProductOption[]>>(
        (acc, item) => {
            acc[item.key] = [];
            return acc;
        },
        {}
    );
};

const buildPackageDate = (
    selectedEvent: AvailabilityProduct,
    selectedTime: number
): PeriodData | undefined => {
    const matchedEvent =
        selectedEvent.events.find((event) => event.time === selectedTime) ||
        selectedEvent.events[0];
    const eventDate = matchedEvent?.event?.date as
        | {
              start: number;
              end?: number;
              timezone?: { name?: string; offset?: number };
          }
        | undefined;

    if (eventDate?.start) {
        const timezoneName = eventDate.timezone?.name;

        return {
            start: eventDate.start,
            timezone: timezoneName
                ? {
                      name: timezoneName as TimezoneName,
                      offset:
                          eventDate.timezone?.offset ??
                          getTimezoneOffset(timezoneName, eventDate.start),
                  }
                : undefined,
        };
    }

    if (!matchedEvent?.time) return undefined;

    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
        start: matchedEvent.time,
        timezone: {
            name: timezoneName as TimezoneName,
            offset: getTimezoneOffset(timezoneName, matchedEvent.time),
        },
    };
};

export const buildVehicleByProductIdDefaults = (
    rows: CapacityFormRow[]
): Record<string, string> => {
    return rows.reduce<Record<string, string>>((acc, row) => {
        acc[row.key] = row.defaultCapacityValue;
        return acc;
    }, {});
};

export const buildPackageDefaultValues = (
    selectedEvent: AvailabilityProduct,
    selectedTime: number,
    pax: PaxData
): PackageBookingFormValues => {
    const selectedEventDiscount = (
        selectedEvent as AvailabilityProduct & {
            discount?: DiscountData | null;
        }
    ).discount;
    const discountType = selectedEventDiscount?.type ?? DiscountType.RELATIVE;
    const discountAmount = Number(selectedEventDiscount?.amount ?? 0);
    const rows = buildCapacityFormRows(selectedEvent);
    const itemsMeta = buildPackageItemsMeta(selectedEvent);

    return {
        package_product_id: selectedEvent.id,
        status: BookingStatus.HOLD,
        date: buildPackageDate(selectedEvent, selectedTime),
        selectedTime: resolveDefaultSelectedTime(
            selectedTime,
            buildPackageAvailableTimeOptions(selectedEvent)
        ),
        pax,
        notes: [],
        vehicleByProductId: buildVehicleByProductIdDefaults(rows),
        selectedOptionsByItemKey:
            buildSelectedOptionsByItemKeyDefaults(itemsMeta),
        discount: {
            type: discountType,
            amount: Number.isNaN(discountAmount) ? 0 : discountAmount,
        },
        sourceType: SourceType.DIRECT,
        sourceName: undefined,
        referral: undefined,
    };
};

const normalizeDiscount = (
    discount: DiscountData
): DiscountData | undefined => {
    if (!discount) return undefined;
    const amount = Number(discount?.amount ?? 0);
    return {
        type: discount.type,
        amount,
    };
};

const mapSourceToDto = (
    values: PackageBookingFormValues
): CreatePackageBookingsReqDto['source'] => {
    if (values.sourceType === SourceType.REFERRAL) {
        return {
            type: 'referral',
            member_id: values.referral?.id,
        };
    }

    if (values.sourceName) {
        return {
            type: 'direct',
            name: values.sourceName,
        };
    }

    return undefined;
};

const mapCustomerItems = (
    customer?: Customer
): CreateBookingReqProductOptionsItemCustomersItemDto[] => {
    if (!customer?.id) return [];

    return [
        {
            customer_id: customer.id,
            plus_x: 0,
        } as CreateBookingReqProductOptionsItemCustomersItemDto,
    ];
};

const mapItemNotes = (notes: PackageBookingFormValues['notes']) => ({
    items: notes.map((note) => ({
        id: '',
        content: note.value,
        visibility: note.type,
    })),
});

export const toCreatePackageBookingsReqDto = (
    values: PackageBookingFormValues,
    selectedEvent: AvailabilityProduct
): CreatePackageBookingsReqDto => {
    const selectedTime = Number(values.selectedTime);
    const customerItems = mapCustomerItems(values.customer);
    const itemsMeta = buildPackageItemsMeta(selectedEvent);
    const items = itemsMeta
        .filter((item) => item.time === selectedTime)
        .map((item) => {
            const selectedOptions =
                values.selectedOptionsByItemKey[item.key] || [];

            return {
                day_index: item.day_index,
                order_index: item.order_index,
                start: item.start,
                capacity_option_id:
                    item.vehicleToCapacityOptionId[
                        values.vehicleByProductId[item.key] || ''
                    ] || undefined,
                product: item.product,
                options: {
                    items: selectedOptions.map((option) => ({
                        id: option.optionId,
                        customers: {
                            pax: option.pax,
                            items: customerItems,
                        },
                    })),
                },
                notes: mapItemNotes(values.notes),
            };
        });

    if (!items.length) {
        throw new Error('No package items available for selected time');
    }

    return {
        package_product_id: values.package_product_id,
        status: values.status,
        source: mapSourceToDto(values),
        notes: null,
        integration: null,
        discount: normalizeDiscount(values.discount),
        date: values.date,
        customers: {
            pax: values.pax,
            items: customerItems,
        },
        items,
    };
};

export const toComputePackagePriceDto = (
    values: PackageBookingFormValues,
    selectedEvent: AvailabilityProduct
): ComputePackagePriceBookingsReqDto | undefined => {
    if (!values.date?.start) return undefined;

    const selectedTime = Number(values.selectedTime);
    const customerItems = mapCustomerItems(values.customer);
    const itemsMeta = buildPackageItemsMeta(selectedEvent);
    const items = itemsMeta
        .filter((item) => item.time === selectedTime)
        .map((item) => {
            const capacityOptionId =
                item.vehicleToCapacityOptionId[
                    values.vehicleByProductId[item.key] || ''
                ];

            if (!capacityOptionId) return undefined;

            const selectedOptions =
                values.selectedOptionsByItemKey[item.key] || [];

            return {
                day_index: item.day_index,
                order_index: item.order_index,
                start: item.start,
                capacity_option_id: capacityOptionId,
                product: item.product,
                options: {
                    items: selectedOptions.map((option) => ({
                        id: option.optionId,
                        customers: {
                            pax: option.pax,
                            items: customerItems,
                        },
                    })),
                },
            };
        });

    if (!items.length || items.some((item) => !item)) {
        return undefined;
    }

    return {
        package_product_id: values.package_product_id,
        date: values.date,
        customers: {
            pax: values.pax,
            items: customerItems,
        },
        pricing: normalizeDiscount(values.discount)
            ? {
                  discount: normalizeDiscount(values.discount),
              }
            : undefined,
        items: items.filter(
            (item): item is NonNullable<(typeof items)[number]> => !!item
        ),
    };
};
